import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Button, message, Typography } from 'antd';
import { PlayCircleFilled, VideoCameraOutlined, FontSizeOutlined, LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { useGeneration } from '../../../context/GenerationContext';

const { Text } = Typography;

const VeoNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const appEdges = getEdges();
            const appNodes = getNodes();

            // 1. Get Prompt (Left Top)
            const promptEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-top');
            let prompt = data.prompt || "";
            if (promptEdge) {
                const sourceNode = appNodes.find(n => n.id === promptEdge.source);
                if (sourceNode?.data?.text) {
                    prompt = sourceNode.data.text;
                }
            }

            if (!prompt) {
                message.warning('Please provide a prompt via the text input or connection.');
                setGenerating(false);
                return;
            }

            const payload = {
                prompt,
            };

            const response = await api.post('/fal/veo', payload);
            const { request_id } = response.data;
            const modelId = 'fal-ai/veo3.1';

            trackGeneration(request_id, modelId, {
                prompt: 'Veo3 Video',
                type: 'Video',
                canvas_id: canvasId,
                node_id: id,
                promptProp: prompt
            }, {
                onComplete: async (result) => {
                    if (result && result.video && result.video.url) {
                        let videoUrl = result.video.url;
                        updateData('src', videoUrl);
                        message.success('Video generated successfully!');
                    } else {
                        message.error('Failed to generate video.');
                    }
                    setGenerating(false);
                },
                onFail: (err) => {
                    console.error("Video Generation failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error(error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false);
        }
    };

    const clearVideo = (e) => {
        e.stopPropagation();
        updateData('src', null);
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={300}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Veo3 Video'}
                iconType={data.iconType || 'video-camera'}
                color={data.nodeColor || TYPE_COLORS.video}
                onTitleChange={onTitleChange}
            />

            {/* Input Handles */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-top"
                style={{
                    ...getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible),
                    top: '50%'
                }}
            />

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                style={getHandleStyle(TYPE_COLORS.video, 'right', handlesVisible)}
            />

            {/* Generate Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 48 }} /> : <PlayCircleFilled style={{ fontSize: 48 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -25,
                    bottom: -25,
                    width: 70,
                    height: 70,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '4px solid #161b22',
                    background: '#f5222d', // Red for video/recording vibe
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', background: '#000' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #f5222d' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #f5222d, 0 0 30px rgba(245, 34, 45, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    borderRadius: 8,
                    animation: generating ? 'pulse-glow 2s infinite' : 'none'
                }}
            >
                <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {data.src ? (
                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                            <video
                                controls
                                src={data.src}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                            <div
                                className="nodrag delete-btn"
                                onClick={clearVideo}
                                style={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    background: 'rgba(0,0,0,0.6)',
                                    borderRadius: '50%',
                                    width: 32,
                                    height: 32,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 20
                                }}
                            >
                                <DeleteOutlined style={{ color: '#fff', fontSize: 16 }} />
                            </div>
                        </div>
                    ) : (
                        <div style={{ color: '#8b949e', textAlign: 'center', padding: 20 }}>
                            <VideoCameraOutlined style={{ fontSize: 48, marginBottom: 10 }} />
                            <div>Connect Text Prompt</div>
                            <div style={{ fontSize: 12, marginTop: 5, color: '#555' }}>
                                Veo3 High Quality Video
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            <style>{`
                 @keyframes pulse-glow {
                    0% {
                        box-shadow: 0 0 15px #f5222d, 0 0 30px rgba(245, 34, 45, 0.4);
                        border-color: #f5222d;
                    }
                    50% {
                        box-shadow: 0 0 25px #ff4d4f, 0 0 50px rgba(245, 34, 45, 0.6);
                        border-color: #ff4d4f;
                    }
                    100% {
                        box-shadow: 0 0 15px #f5222d, 0 0 30px rgba(245, 34, 45, 0.4);
                        border-color: #f5222d;
                    }
                }
            `}</style>
        </div>
    );
};

export default memo(VeoNode);
